import { Component } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Граница ошибок вокруг страниц.
 *
 * ЗАЧЕМ. Без неё любая невыловленная ошибка в рендере сносит всё дерево React,
 * и человек видит ПУСТУЮ БЕЛУЮ СТРАНИЦУ — без единого слова о том, что
 * произошло и что делать. Именно на это пришла жалоба 04.10.2026.
 *
 * Пустой экран плох вдвойне: он выглядит как «сайт умер», хотя чаще всего
 * достаточно перезагрузки, и он ничего не оставляет в консоли для разбора.
 * Поэтому здесь: показываем текст, кнопку перезагрузки и путь домой, а саму
 * ошибку пишем в консоль, чтобы её можно было прислать.
 */
function Fallback({ onReload }) {
  const { t } = useTranslation()
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-5 px-8 py-24 text-center">
      <img src="/brand/urbanpurr-mark.svg" alt="" className="w-40" />
      <p className="text-lg font-bold text-muted">{t('common.error')}</p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          onClick={onReload}
          className="cursor-pointer rounded-full bg-ink px-6 py-3 font-extrabold text-on-ink"
        >
          {t('common.reload')}
        </button>
        <a href="/" className="rounded-full border-[1.5px] border-edge px-6 py-3 font-extrabold text-ink no-underline">
          {t('common.backHome')}
        </a>
      </div>
    </div>
  )
}

export default class ErrorBoundary extends Component {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error, info) {
    // Оставляем след в консоли: без него о поломке можно узнать только со слов
    // пользователя, а повторить её на свежей вкладке обычно не выходит.
    console.error('UrbanPurr: страница упала', error, info?.componentStack)
  }

  /** Сброс при переходе на другой маршрут: иначе одна упавшая страница гасит весь сайт до перезагрузки. */
  componentDidUpdate(prev) {
    if (this.state.failed && prev.resetKey !== this.props.resetKey) {
      this.setState({ failed: false })
    }
  }

  render() {
    if (this.state.failed) return <Fallback onReload={() => window.location.reload()} />
    return this.props.children
  }
}
